import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/Button';

const CARD =
    'bg-gray-900/80 border border-gray-800/90 rounded-2xl shadow-inner shadow-black/40';
const PANEL =
    'bg-gradient-to-b from-gray-950 via-gray-950/95 to-gray-900 border border-gray-800/90 rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.65)]';

const TONE = {
    success: {
        iconWrap: 'bg-emerald-500/15 border border-emerald-400/40',
        icon: 'text-emerald-300',
        value: 'text-emerald-300'
    },
    warning: {
        iconWrap: 'bg-amber-500/15 border border-amber-400/40',
        icon: 'text-amber-300',
        value: 'text-amber-300'
    },
    primary: {
        iconWrap: 'bg-cyan-500/15 border border-cyan-400/40',
        icon: 'text-cyan-300',
        value: 'text-cyan-300'
    },
    error: {
        iconWrap: 'bg-red-500/15 border border-red-400/40',
        icon: 'text-red-300',
        value: 'text-red-300'
    }
};

const safeSum = (obj) => {
    if (!obj || typeof obj !== 'object') return 0;
    return Object.values(obj).reduce((a, b) => a + (Number(b) || 0), 0);
};

const prettyKey = (key) =>
    String(key)
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .trim()
        .toLowerCase();

const EmptyState = ({ title = 'Sin datos', desc = 'Ajusta el rango o carga información.' }) => (
    <div className={CARD + ' p-6 flex items-center justify-center'}>
        <div className="text-center max-w-md">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-3">
                <Icon name="Database" size={22} className="text-cyan-300" />
            </div>
            <p className="text-white font-semibold">{title}</p>
            <p className="text-sm text-gray-400 mt-1">{desc}</p>
        </div>
    </div>
);

const Line = ({ formatCurrency, label, value }) => {
    const n = Number(value) || 0;
    return (
        <div className="flex justify-between items-center py-2 border-b border-gray-800/70">
            <span className="text-xs sm:text-sm text-gray-400 capitalize truncate">{label}</span>
            <span className={`text-xs sm:text-sm font-mono font-medium ${n < 0 ? 'text-red-300' : 'text-gray-200'}`}>
                {formatCurrency(n)}
            </span>
        </div>
    );
};

const SectionCard = ({ icon, title, tone = 'primary', children }) => {
    const st = TONE[tone] ?? TONE.primary;
    return (
        <div className={CARD + ' p-4 sm:p-6'}>
            <div className="flex items-center gap-2 mb-4">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${st.iconWrap}`}>
                    <Icon name={icon} size={18} className={st.icon} />
                </div>
                <div className="min-w-0">
                    <h4 className="text-sm sm:text-base font-semibold text-white">{title}</h4>
                    <p className="text-[11px] text-gray-400">Detalle por rubro</p>
                </div>
            </div>
            {children}
        </div>
    );
};

/**
 * cashFlowData esperado:
 * {
 *   operating: { ... },
 *   investing: { ... },
 *   financing: { ... }
 * }
 */
const CashFlow = ({
    dateRange,
    onDateRangeChange, // opcional
    onExport,

    cashFlowData,  // ✅ viene real del backend/store
    loading = false,
    error = null,

    // opcional:
    locale = 'es-MX',
    defaultCurrency = 'MXN',

    // opcional si quieres calcular ratios reales:
    totalsBase = null
    // totalsBase = { revenue: number, interestExpense: number }
}) => {
    const [currency, setCurrency] = useState(defaultCurrency);

    const formatCurrency = (value) =>
        new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: 0
        }).format(Number(value) || 0);

    const hasData =
        !!cashFlowData?.operating || !!cashFlowData?.investing || !!cashFlowData?.financing;

    const totals = useMemo(() => {
        const totalOperating = safeSum(cashFlowData?.operating);
        const totalInvesting = safeSum(cashFlowData?.investing);
        const totalFinancing = safeSum(cashFlowData?.financing);
        const netCashFlow = totalOperating + totalInvesting + totalFinancing;

        return { totalOperating, totalInvesting, totalFinancing, netCashFlow };
    }, [cashFlowData]);

    const netTone = totals.netCashFlow >= 0 ? 'success' : 'error';

    if (loading) {
        return (
            <div className="space-y-4">
                <div className={PANEL + ' p-5'}>
                    <div className="h-5 w-48 bg-gray-800/70 rounded-lg" />
                    <div className="h-4 w-72 bg-gray-900/70 rounded-lg mt-2" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className={CARD + ' p-5'}>
                            <div className="h-11 w-11 bg-gray-800/60 rounded-xl mb-3" />
                            <div className="h-7 w-40 bg-gray-800/60 rounded-lg" />
                            <div className="h-4 w-32 bg-gray-900/60 rounded-lg mt-2" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return <EmptyState title="Error al cargar" desc={String(error)} />;
    }

    if (!hasData) {
        return <EmptyState title="Flujo sin datos" desc="No hay información para este rango." />;
    }

    const operatingEntries = Object.entries(cashFlowData?.operating ?? {});
    const investingEntries = Object.entries(cashFlowData?.investing ?? {});
    const financingEntries = Object.entries(cashFlowData?.financing ?? {});

    // Ratios (solo si me pasas base real; si no, mostramos “—”)
    const revenue = Number(totalsBase?.revenue) || null;
    const interestExpense = Number(totalsBase?.interestExpense) || null;

    const opToSales =
        revenue && revenue !== 0 ? ((totals.totalOperating / revenue) * 100).toFixed(1) + '%' : '—';

    const interestCoverage =
        interestExpense && interestExpense !== 0
            ? (totals.totalOperating / interestExpense).toFixed(1) + 'x'
            : '—';

    const opMargin =
        revenue && revenue !== 0 ? ((totals.totalOperating / revenue) * 100).toFixed(1) + '%' : '—';

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className={PANEL + ' p-4 sm:p-5'}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg sm:text-xl font-bold text-white truncate">Flujo de Efectivo</h3>
                            <span className="inline-flex text-[11px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/40">
                                {dateRange?.start || '—'} → {dateRange?.end || '—'}
                            </span>
                        </div>
                        <p className="text-cyan-200/80 text-xs sm:text-sm truncate">
                            Operación • Inversión • Financiamiento
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2 justify-end">
                        <select
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            className="bg-gray-950/50 border border-gray-800 rounded-xl px-3 py-2 text-xs sm:text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                        >
                            <option value="MXN">MXN $</option>
                            <option value="USD">USD $</option>
                            <option value="ARS">ARS $</option>
                        </select>

                        <Button
                            variant="outline"
                            onClick={() => onExport?.('cash-flow')}
                            className="border-gray-700 text-gray-300 hover:bg-gray-900 hover:text-white"
                        >
                            <Icon name="Download" className="w-4 h-4 mr-2" />
                            Exportar
                        </Button>
                    </div>
                </div>
            </div>

            {/* Métricas rápidas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                    { label: 'Flujo Operativo', value: totals.totalOperating, icon: 'RefreshCw', tone: 'success' },
                    { label: 'Flujo Inversión', value: totals.totalInvesting, icon: 'TrendingUp', tone: 'warning' },
                    { label: 'Flujo Financiamiento', value: totals.totalFinancing, icon: 'TrendingDown', tone: 'primary' },
                    { label: 'Flujo Neto', value: totals.netCashFlow, icon: 'DollarSign', tone: netTone }
                ].map((m, i) => {
                    const st = TONE[m.tone] ?? TONE.primary;
                    return (
                        <motion.div
                            key={m.label}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.06 }}
                            className={CARD + ' p-4 sm:p-5'}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${st.iconWrap}`}>
                                    <Icon name={m.icon} size={20} className={st.icon} />
                                </div>
                            </div>

                            <p className="text-xl sm:text-2xl font-bold text-white mb-1 font-mono">
                                {formatCurrency(m.value)}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-400">{m.label}</p>
                        </motion.div>
                    );
                })}
            </div>

            {/* Detalle */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <SectionCard icon="RefreshCw" title="Actividades Operativas" tone="success">
                    <div className="space-y-0">
                        {operatingEntries.length === 0 ? (
                            <p className="text-xs text-gray-500">Sin rubros</p>
                        ) : (
                            operatingEntries.map(([k, v]) => (
                                <Line key={k} formatCurrency={formatCurrency} label={prettyKey(k)} value={v} />
                            ))
                        )}
                    </div>

                    <div className="flex justify-between items-center pt-3 mt-2 border-t border-gray-800/80">
                        <span className="text-sm font-semibold text-gray-100">Flujo Neto Operativo</span>
                        <span className="text-sm sm:text-lg font-bold font-mono text-emerald-300">
                            {formatCurrency(totals.totalOperating)}
                        </span>
                    </div>
                </SectionCard>

                <SectionCard icon="TrendingUp" title="Actividades de Inversión" tone="warning">
                    <div className="space-y-0">
                        {investingEntries.length === 0 ? (
                            <p className="text-xs text-gray-500">Sin rubros</p>
                        ) : (
                            investingEntries.map(([k, v]) => (
                                <Line key={k} formatCurrency={formatCurrency} label={prettyKey(k)} value={v} />
                            ))
                        )}
                    </div>

                    <div className="flex justify-between items-center pt-3 mt-2 border-t border-gray-800/80">
                        <span className="text-sm font-semibold text-gray-100">Flujo Neto de Inversión</span>
                        <span className="text-sm sm:text-lg font-bold font-mono text-amber-300">
                            {formatCurrency(totals.totalInvesting)}
                        </span>
                    </div>
                </SectionCard>

                <SectionCard icon="TrendingDown" title="Actividades de Financiamiento" tone="primary">
                    <div className="space-y-0">
                        {financingEntries.length === 0 ? (
                            <p className="text-xs text-gray-500">Sin rubros</p>
                        ) : (
                            financingEntries.map(([k, v]) => (
                                <Line key={k} formatCurrency={formatCurrency} label={prettyKey(k)} value={v} />
                            ))
                        )}
                    </div>

                    <div className="flex justify-between items-center pt-3 mt-2 border-t border-gray-800/80">
                        <span className="text-sm font-semibold text-gray-100">Flujo Neto de Financiamiento</span>
                        <span className="text-sm sm:text-lg font-bold font-mono text-cyan-300">
                            {formatCurrency(totals.totalFinancing)}
                        </span>
                    </div>
                </SectionCard>
            </div>

            {/* Resumen neto */}
            <div className="bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-indigo-500/10 border border-cyan-500/20 rounded-2xl p-4 sm:p-6 shadow-[0_25px_80px_rgba(8,47,73,0.55)]">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h4 className="text-sm sm:text-base font-semibold text-white mb-1">Flujo de Efectivo Neto</h4>
                        <p className="text-xs sm:text-sm text-gray-300/80">
                            Cambio neto en efectivo del período
                        </p>
                    </div>

                    <div className="text-right">
                        <p className={`text-2xl sm:text-3xl font-bold font-mono ${TONE[netTone].value}`}>
                            {formatCurrency(totals.netCashFlow)}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-400">
                            {totals.netCashFlow >= 0 ? 'Entrada neta de efectivo' : 'Salida neta de efectivo'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Análisis de liquidez (sin números inventados) */}
            <div className={CARD + ' p-4 sm:p-6'}>
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-cyan-500/15 border border-cyan-400/40">
                        <Icon name="Activity" size={18} className="text-cyan-300" />
                    </div>
                    <div>
                        <h4 className="text-sm sm:text-base font-semibold text-white">Análisis de Liquidez</h4>
                        <p className="text-[11px] text-gray-400">Ratios (requiere base real)</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                        {
                            label: 'Flujo Operativo / Ventas',
                            value: opToSales,
                            description: 'Eficiencia en conversión a efectivo'
                        },
                        {
                            label: 'Cobertura de Intereses',
                            value: interestCoverage,
                            description: 'Capacidad de pago de intereses'
                        },
                        {
                            label: 'Margen de Flujo Operativo',
                            value: opMargin,
                            description: 'Generación de efectivo por ventas'
                        }
                    ].map((m, idx) => (
                        <motion.div
                            key={m.label}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.04 }}
                            className="text-center p-3 sm:p-4 bg-gray-950/60 border border-gray-800 rounded-2xl"
                        >
                            <p className="text-lg sm:text-2xl font-bold text-white mb-1 font-mono">{m.value}</p>
                            <p className="text-xs sm:text-sm font-medium text-gray-100">{m.label}</p>
                            <p className="text-[11px] text-gray-400">{m.description}</p>
                        </motion.div>
                    ))}
                </div>

                <p className="mt-3 text-[11px] text-gray-500">
                    * Para mostrar estos ratios con valores, pásame <span className="text-gray-300">totalsBase</span> (revenue e interestExpense).
                </p>
            </div>
        </div>
    );
};

export default CashFlow;
